import { volumetricLwc } from './evolution.ts';
import type { SnowpackState } from './scene.ts';
import { GRAIN_LABELS, type Grain } from './snowpack.ts';
import {
  AVALANCHE_PROBLEM_LABELS,
  columnStability,
  handHardness,
  type AvalancheProblem,
} from './stability.ts';

/** One layer of a snow profile, as shown in a snow-pit style view. */
export interface ProfileLayer {
  /** Height of the layer's bottom and top above the ground (m, vertical). */
  bottom: number;
  top: number;
  thickness: number;
  density: number;
  grain: Grain;
  grainLabel: string;
  hardness: 'F' | '4F' | '1F' | 'P' | 'K';
  /** Hours since the layer was deposited, at the evaluation time. */
  ageHours: number;
  /** Liquid water content, % by volume. */
  lwcPct: number;
  strengthKPa: number;
  /** Stability indices with this layer as the failure layer (capped at 9.99). */
  natural: number;
  skier: number;
  /** A slab lies on top of this layer (≥ 10 cm). */
  hasSlab: boolean;
  critical: boolean;
}

export interface SnowProfile {
  cell: number;
  elevation: number;
  slope: number;
  /** True bearing, NaN on flat ground. */
  aspect: number;
  airTemp: number;
  /** Total snow depth (m). */
  hs: number;
  /** Layers top-down (surface first). */
  layers: ProfileLayer[];
  /** Index into `layers` of the critical layer, or −1. */
  critical: number;
  problem: AvalancheProblem;
  problemLabel: string;
}

/** Snow profile of one snowpack cell at the state's evaluation time. */
export function snowProfile(state: SnowpackState, cell: number): SnowProfile {
  const { columns: cols, topo, evaluationHour } = state;
  const slope = topo.slope.data[cell]!;
  const airTemp = state.airTemp[cell]!;
  const cs = columnStability(cols, cell, slope, airTemp, evaluationHour);
  let hs = 0;
  for (const l of cs.layers) hs += l.thickness;
  const layers: ProfileLayer[] = cs.layers.map((l, i) => {
    const grain = cols.grain[l.layer]! as Grain;
    const density = cols.density[l.layer]!;
    return {
      top: hs - l.depthTop,
      bottom: hs - l.depthTop - l.thickness,
      thickness: l.thickness,
      density,
      grain,
      grainLabel: GRAIN_LABELS[grain],
      hardness: handHardness(density),
      ageHours: evaluationHour - cols.depositedAt[l.layer]!,
      lwcPct: volumetricLwc(cols, l.layer) * 100,
      strengthKPa: l.strengthKPa,
      natural: l.natural,
      skier: l.skier,
      hasSlab: l.isSlab,
      critical: i === cs.critical,
    };
  });
  const problem = state.stability.problem[cell]! as AvalancheProblem;
  return {
    cell,
    elevation: state.elevation.data[cell]!,
    slope,
    aspect: topo.aspect.data[cell]!,
    airTemp,
    hs,
    layers,
    critical: cs.critical,
    problem,
    problemLabel: AVALANCHE_PROBLEM_LABELS[problem],
  };
}
